import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { ApiError } from './client'
import { notify, notifyError } from '@/lib/notify'
import { connectPlatform } from '@/lib/oauth'
import { platformName } from '@/lib/text'
import { useAuthStore } from '@/store/auth'

/**
 * The Quasar client raised a Notify on every failed request. That behaviour is
 * kept, but centralised here rather than repeated in four copy-pasted verb
 * methods — and a 401 now also clears the stored token, which the original
 * never did (it left users stuck on a dead session until they cleared storage).
 */
function handleError(error: unknown, silent = false): void {
  if (error instanceof ApiError && error.isUnauthorized) {
    useAuthStore.getState().clear()
  }
  if (silent) return

  // A refusal that names an external platform is something the user can fix:
  // entering a giveaway needs a linked Discord account, and before this the
  // toast said so with no way to act on it. The link comes from the API
  // (external_platform.auth_url), so a new platform needs no client release.
  if (error instanceof ApiError) {
    const platform = error.linkRequired
    if (platform) {
      notify({
        message: error.message,
        color: 'warning',
        duration: 12_000,
        action: {
          label: `Connect ${platformName(platform.name)}`,
          onClick: () => void connectPlatform(platform),
        },
      })
      return
    }
  }

  notifyError(error instanceof Error ? error.message : 'Something went wrong.')
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        // A background refetch that fails while cached data is still on screen
        // should not interrupt the user with a toast. Queries can also opt out,
        // like mutations do, when a failure isn't worth one.
        handleError(error, query.state.data !== undefined || query.meta?.silentError === true)
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        // Mutations opt out when they render the failure themselves.
        handleError(error, mutation.meta?.silentError === true)
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: (failureCount, error) => {
          if (error instanceof ApiError) {
            if (error.isUnauthorized) return false
            if (error.status !== undefined && error.status >= 400 && error.status < 500)
              return false
          }
          return failureCount < 2
        },
        refetchOnWindowFocus: true,
      },
      mutations: {
        retry: false,
      },
    },
  })
}

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: { silentError?: boolean }
  }
}
